"use client"

import { useState, useMemo } from "react"
import { RotateCcw, Target } from "lucide-react"
import type { Idioma } from "@/lib/landing/idioma"
import { eurosEn, numeroEn } from "./formato"

const defaultState = {
  ingresosObjetivo: 50000,
  diasApertura: 26,
  serviciosDia: 2,
  cubiertosServicio: 40,
}

const T = {
  es: {
    titulo: "Objetivos del mes",
    limpiar: "Limpiar",
    ingresosObjetivo: "Ingresos objetivo mensuales (€)",
    diasApertura: "Días de apertura al mes",
    serviciosDia: "Servicios por día",
    servicios: (n: number) => `${n} ${n === 1 ? "servicio" : "servicios"}`,
    cubiertosServicio: "Cubiertos por servicio",
    formulaEtiqueta: "Fórmula:",
    formula: "Ticket Medio = Ingresos Objetivo ÷ (Días × Servicios × Cubiertos)",
    necesario: "Ticket Medio Necesario",
    objetivo: "Ticket medio objetivo",
    paraAlcanzar: (cifra: string) => `Para alcanzar ${cifra}/mes`,
    ingresosDiarios: "Ingresos diarios",
    porServicio: "Por servicio",
    cubiertosMes: "Cubiertos/mes",
    cubiertosSemana: "Cubiertos/semana",
    rangosTitulo: "Tu ticket objetivo se sitúa en:",
    rangos: ["Fast casual / Cafetería", "Casual dining", "Restaurante medio-alto", "Fine dining / Gastronómico"],
    cifrasRangos: ["< 15€", "15€ - 25€", "25€ - 40€", "> 40€"],
    consejosTitulo: "💡 Aumentar ticket medio",
    consejos: [
      "Sugiere entrantes y postres (upselling)",
      "Ofrece maridajes de vino",
      "Menús degustación con margen alto",
      "Café y sobremesa tras comida",
    ],
  },
  en: {
    titulo: "This month's targets",
    limpiar: "Reset",
    ingresosObjetivo: "Monthly revenue target (€)",
    diasApertura: "Trading days per month",
    serviciosDia: "Services per day",
    servicios: (n: number) => `${n} ${n === 1 ? "service" : "services"}`,
    cubiertosServicio: "Covers per service",
    formulaEtiqueta: "Formula:",
    formula: "Average spend per head = Revenue target ÷ (Days × Services × Covers)",
    necesario: "Average spend you need",
    objetivo: "Target average spend per head",
    paraAlcanzar: (cifra: string) => `To reach ${cifra}/month`,
    ingresosDiarios: "Daily revenue",
    porServicio: "Per service",
    cubiertosMes: "Covers/month",
    cubiertosSemana: "Covers/week",
    rangosTitulo: "Your target spend puts you in:",
    rangos: ["Fast casual / Café", "Casual dining", "Mid to upper-range restaurant", "Fine dining"],
    cifrasRangos: ["< €15", "€15 - €25", "€25 - €40", "> €40"],
    consejosTitulo: "💡 Raising your average spend",
    consejos: [
      "Suggest starters and desserts (upselling)",
      "Offer wine pairings",
      "High-margin tasting menus",
      "Coffee and after-dinner drinks",
    ],
  },
}

export default function TicketMedioCalculator({ idioma = "es" }: { idioma?: Idioma }) {
  const t = T[idioma]
  const en = idioma === "en"
  const [ingresosObjetivo, setIngresosObjetivo] = useState(defaultState.ingresosObjetivo)
  const [diasApertura, setDiasApertura] = useState(defaultState.diasApertura)
  const [serviciosDia, setServiciosDia] = useState(defaultState.serviciosDia)
  const [cubiertosServicio, setCubiertosServicio] = useState(defaultState.cubiertosServicio)

  const results = useMemo(() => {
    const cubiertosTotal = diasApertura * serviciosDia * cubiertosServicio
    const ticketMedioNecesario = cubiertosTotal > 0 ? ingresosObjetivo / cubiertosTotal : 0

    const ingresosDiarios = diasApertura > 0 ? ingresosObjetivo / diasApertura : 0
    const ingresosPorServicio = serviciosDia > 0 ? ingresosDiarios / serviciosDia : 0

    // Breakdown
    const cubiertosSemanales = (diasApertura / 4) * serviciosDia * cubiertosServicio
    const ingresosSemanales = ingresosObjetivo / 4

    return {
      cubiertosTotal,
      ticketMedioNecesario,
      ingresosDiarios,
      ingresosPorServicio,
      cubiertosSemanales,
      ingresosSemanales,
    }
  }, [ingresosObjetivo, diasApertura, serviciosDia, cubiertosServicio])

  const reset = () => {
    setIngresosObjetivo(defaultState.ingresosObjetivo)
    setDiasApertura(defaultState.diasApertura)
    setServiciosDia(defaultState.serviciosDia)
    setCubiertosServicio(defaultState.cubiertosServicio)
  }

  const getTicketColor = (ticket: number) => {
    if (ticket < 15) return "text-emerald-600"
    if (ticket < 25) return "text-lime-600"
    if (ticket < 40) return "text-amber-600"
    return "text-violet-600"
  }

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
      <div className="grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-zinc-200">
        {/* Inputs */}
        <div className="p-6 md:p-8 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-zinc-900">{t.titulo}</h2>
            <button
              onClick={reset}
              className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              {t.limpiar}
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.ingresosObjetivo}
            </label>
            <input
              type="number"
              value={ingresosObjetivo || ""}
              onChange={(e) => setIngresosObjetivo(parseFloat(e.target.value) || 0)}
              placeholder="50000"
              min="0"
              step="1000"
              className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.diasApertura}
            </label>
            <input
              type="number"
              value={diasApertura || ""}
              onChange={(e) => setDiasApertura(parseInt(e.target.value) || 0)}
              placeholder="26"
              min="1"
              max="31"
              className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.serviciosDia}
            </label>
            <div className="flex gap-2">
              {[1, 2, 3].map((n) => (
                <button
                  key={n}
                  onClick={() => setServiciosDia(n)}
                  className={`flex-1 py-2.5 px-4 rounded-xl font-medium transition-all ${
                    serviciosDia === n
                      ? "bg-lime-500 text-white"
                      : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
                  }`}
                >
                  {t.servicios(n)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.cubiertosServicio}
            </label>
            <input
              type="number"
              value={cubiertosServicio || ""}
              onChange={(e) => setCubiertosServicio(parseInt(e.target.value) || 0)}
              placeholder="40"
              min="1"
              className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
            />
          </div>

          {/* Info box */}
          <div className="bg-sky-50 border border-sky-200 rounded-xl p-4">
            <p className="text-sm text-sky-800">
              <strong>{t.formulaEtiqueta}</strong> {t.formula}
            </p>
          </div>
        </div>

        {/* Results */}
        <div className="p-6 md:p-8 bg-zinc-50 space-y-6">
          <h2 className="text-lg font-semibold text-zinc-900">{t.necesario}</h2>

          {/* Main KPI */}
          <div className="bg-gradient-to-br from-sky-500 to-sky-600 rounded-2xl p-6 text-white">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-5 h-5 text-sky-100" />
              <p className="text-sm font-medium text-sky-100">{t.objetivo}</p>
            </div>
            <p className="text-5xl font-bold">{en ? eurosEn(results.ticketMedioNecesario) : `${results.ticketMedioNecesario.toFixed(2)}€`}</p>
            <p className="text-sm text-sky-200 mt-2">
              {t.paraAlcanzar(en ? eurosEn(ingresosObjetivo, 0) : `${ingresosObjetivo.toLocaleString()}€`)}
            </p>
          </div>

          {/* Secondary KPIs */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <p className="text-xs text-zinc-500 mb-1">{t.ingresosDiarios}</p>
              <p className="text-2xl font-bold text-zinc-900">
                {en ? eurosEn(results.ingresosDiarios, 0) : `${results.ingresosDiarios.toFixed(0)}€`}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <p className="text-xs text-zinc-500 mb-1">{t.porServicio}</p>
              <p className="text-2xl font-bold text-zinc-900">
                {en ? eurosEn(results.ingresosPorServicio, 0) : `${results.ingresosPorServicio.toFixed(0)}€`}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <p className="text-xs text-zinc-500 mb-1">{t.cubiertosMes}</p>
              <p className="text-2xl font-bold text-zinc-900">
                {en ? numeroEn(results.cubiertosTotal, 0) : results.cubiertosTotal.toLocaleString()}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <p className="text-xs text-zinc-500 mb-1">{t.cubiertosSemana}</p>
              <p className="text-2xl font-bold text-zinc-900">
                {en ? numeroEn(Math.round(results.cubiertosSemanales), 0) : Math.round(results.cubiertosSemanales).toLocaleString()}
              </p>
            </div>
          </div>

          {/* Ticket ranges */}
          <div className="bg-white rounded-xl border border-zinc-200 p-4">
            <h3 className="text-sm font-semibold text-zinc-900 mb-3">{t.rangosTitulo}</h3>
            <div className="space-y-2">
              <div className={`flex justify-between p-2 rounded-lg ${results.ticketMedioNecesario < 15 ? "bg-emerald-50" : ""}`}>
                <span className="text-sm text-zinc-600">{t.rangos[0]}</span>
                <span className="text-sm font-medium text-emerald-600">{t.cifrasRangos[0]}</span>
              </div>
              <div className={`flex justify-between p-2 rounded-lg ${results.ticketMedioNecesario >= 15 && results.ticketMedioNecesario < 25 ? "bg-lime-50" : ""}`}>
                <span className="text-sm text-zinc-600">{t.rangos[1]}</span>
                <span className="text-sm font-medium text-lime-600">{t.cifrasRangos[1]}</span>
              </div>
              <div className={`flex justify-between p-2 rounded-lg ${results.ticketMedioNecesario >= 25 && results.ticketMedioNecesario < 40 ? "bg-amber-50" : ""}`}>
                <span className="text-sm text-zinc-600">{t.rangos[2]}</span>
                <span className="text-sm font-medium text-amber-600">{t.cifrasRangos[2]}</span>
              </div>
              <div className={`flex justify-between p-2 rounded-lg ${results.ticketMedioNecesario >= 40 ? "bg-violet-50" : ""}`}>
                <span className="text-sm text-zinc-600">{t.rangos[3]}</span>
                <span className="text-sm font-medium text-violet-600">{t.cifrasRangos[3]}</span>
              </div>
            </div>
          </div>

          {/* Tips */}
          <div className="bg-lime-50 border border-lime-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-lime-800 mb-2">{t.consejosTitulo}</h3>
            <ul className="text-sm text-lime-700 space-y-1">
              {t.consejos.map((c) => (
                <li key={c}>• {c}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
